<template>
<div class="hris-page">
  <div class="hris-page-inner">
    <div class="hris-page-header">
      <span class="hris-page-eyebrow">System Settings</span>
      <h1 class="hris-page-title">NFC Tag Registration</h1>
      <p class="hris-page-subtitle">Map NFC tags to employees so tablet attendance is traceable.</p>
    </div>

    <div class="hris-form-section">
      <h3 class="hris-form-section-title">Register Tag</h3>
      <q-form ref="tagForm" @submit.prevent="submitTag">
        <div class="hris-form-row">
          <div class="hris-form-field">
            <q-input v-model="form.uid" label="Tag UID" outlined dense color="green" :rules="[formRules.required]"></q-input>
          </div>

          <div class="hris-form-field">
            <q-select
              v-model="form.employee_id"
              :options="employeeOptions"
              option-value="value"
              option-label="label"
              label="Assigned Employee"
              outlined
              dense
              color="green"
              :rules="[formRules.required]"
              emit-value
              map-options
              use-input
            ></q-select>
            <q-banner v-if="employeeOptions.length === 0" class="bg-orange-1 text-orange-9 q-mt-sm">
              No active employees found. Create or activate an employee before assigning a tag.
            </q-banner>
          </div>

          <div class="hris-form-field">
            <q-input v-model="form.label" label="Label (optional)" outlined dense color="green"></q-input>
          </div>

          <div class="hris-form-field">
            <q-input v-model="form.expires_at" type="date" label="Expires At (optional)" outlined dense color="green"></q-input>
          </div>

          <div class="hris-form-field">
            <q-option-group
              v-model="form.is_active"
              :options="[
                { label: 'Active', value: '1' },
                { label: 'Inactive', value: '0' }
              ]"
              inline
              color="green"
              type="radio"
            ></q-option-group>
          </div>
        </div>

        <div class="hris-form-actions">
          <q-btn flat label="Clear" class="hris-q-ghost" type="button" @click="clearForm"></q-btn>
          <q-btn unelevated label="Save Tag" class="hris-q-primary" type="submit" :loading="saving"></q-btn>
        </div>
      </q-form>
    </div>

    <div class="hris-panel">
      <h3 class="hris-panel-title">Registered Tags</h3>
      <q-table
        flat
        :columns="columns"
        :data="tags"
        row-key="id"
        :pagination.sync="pagination"
        :loading="loadingTags"
        loading-label="Loading NFC tags"
      >
        <template v-slot:loading>
          <div class="hris-loading absolute-full flex flex-center" style="background: rgba(255,255,255,0.7); z-index: 1;">
            <q-spinner color="green" size="40px"></q-spinner>
            <div>Loading NFC tags...</div>
          </div>
        </template>
        <template v-slot:body="props">
          <q-tr :props="props">
            <q-td key="counter" :props="props" class="text-center">{{ props.row.counter }}</q-td>
            <q-td key="uid" :props="props">{{ props.row.uid }}</q-td>
            <q-td key="employee_code" :props="props">{{ props.row.employee_code || '-' }}</q-td>
            <q-td key="employee_name" :props="props">{{ props.row.employee_name || '-' }}</q-td>
            <q-td key="label" :props="props">{{ props.row.label || '-' }}</q-td>
            <q-td key="expires_at" :props="props">{{ formatDate(props.row.expires_at) }}</q-td>
            <q-td key="is_active" :props="props" class="text-center">
              <span class="hris-badge" :class="props.row.is_active ? 'hris-badge-success' : 'hris-badge-danger'">
                {{ props.row.is_active ? 'Active' : 'Inactive' }}
              </span>
            </q-td>
            <q-td key="action" :props="props" class="text-center">
              <q-btn round flat dense class="hris-q-ghost" icon="fas fa-edit" @click.stop="setFormForTag(props.row)">
                <q-tooltip anchor="top middle" self="bottom middle">Edit</q-tooltip>
              </q-btn>
              <q-btn round flat dense class="hris-q-ghost" icon="fas fa-trash" @click.stop="prepareDelete(props.row)">
                <q-tooltip anchor="top middle" self="bottom middle">Remove</q-tooltip>
              </q-btn>
            </q-td>
          </q-tr>
        </template>
        <template v-slot:no-data>
          <div class="hris-empty full-width">No NFC tags registered yet.</div>
        </template>
      </q-table>
    </div>

    <q-dialog v-model="deleteDialog" persistent>
      <div class="hris-modal">
        <div class="hris-modal-header">
          <h2 class="hris-modal-title">Remove NFC Tag</h2>
        </div>
        <div class="hris-modal-body">
          Are you sure you want to remove <strong>{{ tagToDelete ? tagToDelete.uid : '' }}</strong>?
        </div>
        <div class="hris-modal-footer">
          <q-btn flat label="Cancel" class="hris-q-ghost" v-close-popup @click="cancelDelete"></q-btn>
          <q-btn unelevated label="Remove" class="hris-q-primary" @click="confirmDeleteTag"></q-btn>
        </div>
      </div>
    </q-dialog>
  </div>
</div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'NfcTagRegistration',
  data() {
    return {
      form: {
        tempID: 0,
        uid: '',
        employee_id: null,
        label: '',
        expires_at: null,
        is_active: '1',
      },
      tags: [],
      employees: [],
      columns: [
        { name: 'counter', label: '#', field: 'counter', align: 'center' },
        { name: 'uid', label: 'Tag UID', field: 'uid' },
        { name: 'employee_code', label: 'Employee ID', field: 'employee_code' },
        { name: 'employee_name', label: 'Assigned Employee', field: 'employee_name' },
        { name: 'label', label: 'Label', field: 'label' },
        { name: 'expires_at', label: 'Expires At', field: 'expires_at' },
        { name: 'is_active', label: 'Status', field: 'is_active', align: 'center' },
        { name: 'action', label: 'Action', field: 'action', align: 'center' },
      ],
      pagination: {
        rowsPerPage: 10,
      },
      loadingTags: false,
      saving: false,
      deleteDialog: false,
      tagToDelete: null,
    };
  },
  computed: {
    employeeOptions() {
      return this.employees.map(emp => {
        const fullname = `${emp.firstname || ''} ${emp.lastname || ''}`.trim();
        const suffix = fullname ? ` · ${this.toTitleCase(fullname)}` : '';
        const employeeLabel = `${emp.employee_id || 'N/A'}${suffix}`;
        return {
          value: emp.id,
          label: employeeLabel,
        };
      });
    },
  },
  created() {
    this.fetchEmployees();
    this.fetchTags();
  },
  methods: {
    fetchEmployees() {
      axios.create({
        baseURL: this.apiUrl,
        headers: { Authorization: `Bearer ${this.token}` },
      })
      .get('/employees', {
        params: {
          status: 'ACTIVE',
          start: 0,
          countPerPage: 1000,
        },
      })
      .then(res => {
        this.employees = res.data.data || [];
      })
      .catch(() => {
        this.employees = [];
      });
    },
    fetchTags() {
      this.loadingTags = true;
      axios.create({
        baseURL: this.apiUrl,
        headers: { Authorization: `Bearer ${this.token}` },
      })
      .get('/admin/nfc/tags')
      .then(res => {
        this.tags = res.data.data || [];
      })
      .catch(() => {
        this.tags = [];
      })
      .finally(() => {
        this.loadingTags = false;
      });
    },
    submitTag() {
      if (this.saving) {
        return;
      }

      this.saving = true;
      const formData = new FormData();
      formData.append('uid', this.form.uid);
      formData.append('employee_id', this.form.employee_id ?? '');
      formData.append('label', this.form.label ?? '');
      formData.append('expires_at', this.form.expires_at ?? '');
      formData.append('is_active', this.form.is_active);
      formData.append('tempID', this.form.tempID);

      axios.create({
        baseURL: this.apiUrl,
        headers: { Authorization: `Bearer ${this.token}` },
      })
      .post('/admin/nfc/tag', formData)
      .then(res => {
        this.$q.notify({ type: 'positive', message: res.data.message || 'NFC tag saved.' });
        this.fetchTags();
        this.clearForm();
      })
      .catch(error => {
        const message = error?.response?.data?.message || 'Unable to save NFC tag.';
        this.$q.notify({ type: 'negative', message });
      })
      .finally(() => {
        this.saving = false;
      });
    },
    setFormForTag(tag) {
      this.form = {
        tempID: tag.id,
        uid: tag.uid,
        employee_id: tag.employee_id,
        label: tag.label,
        expires_at: tag.expires_at ? tag.expires_at.split(' ')[0] : null,
        is_active: tag.is_active ? '1' : '0',
      };
      if (this.$refs.tagForm) {
        this.$refs.tagForm.resetValidation && this.$refs.tagForm.resetValidation();
      }
    },
    clearForm() {
      this.form = {
        tempID: 0,
        uid: '',
        employee_id: null,
        label: '',
        expires_at: null,
        is_active: '1',
      };
      if (this.$refs.tagForm) {
        this.$refs.tagForm.resetValidation && this.$refs.tagForm.resetValidation();
      }
    },
    prepareDelete(tag) {
      this.tagToDelete = tag;
      this.deleteDialog = true;
    },
    cancelDelete() {
      this.tagToDelete = null;
      this.deleteDialog = false;
    },
    confirmDeleteTag() {
      if (!this.tagToDelete) {
        return;
      }

      const formData = new FormData();
      formData.append('id', this.tagToDelete.id);

      axios.create({
        baseURL: this.apiUrl,
        headers: { Authorization: `Bearer ${this.token}` },
      })
      .post('/admin/nfc/tag/delete', formData)
      .then(res => {
        this.$q.notify({ type: 'positive', message: res.data.message || 'NFC tag removed.' });
        this.fetchTags();
      })
      .catch(error => {
        const message = error?.response?.data?.message || 'Unable to remove NFC tag.';
        this.$q.notify({ type: 'negative', message });
      })
      .finally(() => {
        this.cancelDelete();
      });
    },
    formatDate(value) {
      if (!value) {
        return '-';
      }
      return value.split(' ')[0];
    },
  },
};
</script>
